import { useCallback, useEffect, useState } from 'react';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { Field, inputStyle } from '../../components/Field';
import { Spinner } from '../../components/Spinner';
import { apiGet, apiSend, ClientApiError } from '../../api/client';
import type { ReconTeam } from '../../../shared/types';

interface AdminUser {
  id: string;
  fullName: string;
}

export function TeamsScreen() {
  const [teams, setTeams] = useState<ReconTeam[] | null>(null);
  const [reconUsers, setReconUsers] = useState<AdminUser[]>([]);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [teamData, userData] = await Promise.all([
        apiGet<{ teams: ReconTeam[] }>('/api/teams'),
        apiGet<{ users: AdminUser[] }>('/api/users?role=recon&status=active'),
      ]);
      setTeams(teamData.teams);
      setReconUsers(userData.users);
    } catch (caught) {
      setError(caught instanceof ClientApiError ? caught.message : 'Could not load teams.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await action();
      await load();
    } catch (caught) {
      setError(caught instanceof ClientApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell title="Recon Teams">
      {error && <div className="error-banner">{error}</div>}

      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault();
          if (newName.trim() === '') return;
          void run(async () => {
            await apiSend('/api/teams', 'POST', { name: newName });
            setNewName('');
          });
        }}
      >
        <Field label="New team name" htmlFor="team-name">
          <input
            id="team-name"
            style={inputStyle}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </Field>
        <Button type="submit" full disabled={busy || newName.trim() === ''}>
          Create team
        </Button>
      </form>

      {!teams && <Spinner label="Loading teams" />}

      {teams?.map((team) => (
        <div key={team.id} className="card">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong>{team.name}</strong>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => void run(() => apiSend(`/api/teams/${team.id}`, 'PATCH', { active: !team.active }))}
            >
              {team.active ? 'Deactivate' : 'Activate'}
            </Button>
          </div>

          <p className="muted" style={{ margin: '0.4rem 0 0.2rem' }}>
            Members
          </p>
          {reconUsers.length === 0 && <p className="muted">No recon users yet. Add them on the Users screen.</p>}
          {reconUsers.map((user) => {
            const isMember = team.memberIds.includes(user.id);
            return (
              <label key={user.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minHeight: 'var(--tap)' }}>
                <input
                  type="checkbox"
                  checked={isMember}
                  disabled={busy}
                  style={{ width: 20, height: 20 }}
                  onChange={() => {
                    const memberIds = isMember
                      ? team.memberIds.filter((memberId) => memberId !== user.id)
                      : [...team.memberIds, user.id];
                    void run(() => apiSend(`/api/teams/${team.id}`, 'PATCH', { memberIds }));
                  }}
                />
                {user.fullName}
              </label>
            );
          })}
        </div>
      ))}
    </AppShell>
  );
}
